export interface NCssObject { [key: string]: string };
export interface NItemAttributes { [key: string]: string|null };
interface NItemDebug {
    tag: string,
    attributes: NItemAttributes|null,
}
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type DOMRectReturnValue = null|(() => any)|number|undefined;
export interface NAnimationOptions {
    duration?: number,
    displayValue?: string,
};

interface NEventEntry { listener: (e: Event) => void; type: string };
const nEventRegistry = new WeakMap<Element, Map<string, Set<NEventEntry>>>();

export default class NItem
{
    #node: Element;
    #tag: string;
    #boundingClientRect: null|DOMRect = null;
    #runningAnimations: WeakMap<Element, Animation>;

    constructor(node: Element)
    {
        this.#node = node;
        this.#tag = node.tagName;
        this.#runningAnimations = new WeakMap();
    }

    #setBoundingClientRect()
    {
        this.#boundingClientRect = this.#node?.getBoundingClientRect();
    }

    parent(selector: string|null = null): NItem|null
    {
        if (this.#node) {
            const p = this.#node?.parentElement ?? null;
            if (p) {
                if (selector === null && p) {
                    return new NItem(p);
                }
                if (selector) {
                    return p && p.matches(selector) ? new NItem(p) : null;
                }
                return new NItem(p);
            }
        }

        return null;
    }

    #getClientRectValue(key: keyof DOMRect): DOMRectReturnValue
    {
        this.#setBoundingClientRect();
        return this.#boundingClientRect?.[key] ?? 0;
    }

    get y(): number { return this.#getClientRectValue('y') as number; }
    get x(): number { return this.#getClientRectValue('x') as number; }
    get top(): number { return this.#getClientRectValue('top') as number; }
    get bottom(): number { return this.#getClientRectValue('bottom') as number; }
    get left(): number { return this.#getClientRectValue('left') as number; }
    get right(): number { return this.#getClientRectValue('right') as number; }
    get width(): number { return this.#getClientRectValue('width') as number; }
    get height(): number { return this.#getClientRectValue('height') as number; }

    runAnimation(
        keyframes: Keyframe[]|PropertyIndexedKeyframes,
        opts: NAnimationOptions = {}
    )
    {
        this.#runningAnimations.get(this.#node)?.cancel();
        const anim = this.#node.animate(keyframes, { fill: 'forwards', ...opts });
        this.#runningAnimations.set(this.#node, anim);
        return anim.finished;
    }

    show(opts: NAnimationOptions = {})
    {
        (this.#node as HTMLElement).style.setProperty(
            'display',
            (opts?.displayValue) ?? 'block',
        );
        return this.runAnimation([{ opacity: 0 }, { opacity: 1 }], opts);
    }

    hide(opts: NAnimationOptions = {})
    {
        return this.runAnimation([{ opacity: 1 }, { opacity: 0 }], opts);
    }

    css(
        keyOrValues: NCssObject|string,
        value: null|string|number|undefined = undefined
    ): NItem|string
    {
        const set = (typeof keyOrValues === 'string')
            ? { [keyOrValues]: value }
            : keyOrValues;
        for (const k in set) {
            const value = set[k];
            (this.#node as HTMLElement).style.setProperty(
                k,
                value as string
            );
        }
        return this;
    }

    remove()
    {
        this.#node.remove();
    }

    getAttribute(name: string): string|null
    {
        return this.#node?.getAttribute(name) ?? null;
    }

    setAttribute(name: string, value: string): NItem
    {
        this.#node?.setAttribute?.(name, value);
        return this;
    }

    removeAttribute(name: string): NItem
    {
        this.#node?.removeAttribute(name);
        return this;
    }

    getAttributes(): NItemAttributes
    {
        const attributes: NItemAttributes = {};
        const attributeNames: Array<string> = this.#node?.getAttributeNames() ?? [];

        for (const key of attributeNames) {
            const value = this.#node?.getAttribute(key) ?? null;
            attributes[key] = value;
        }

        return attributes;
    }

    html<T extends Element>(content: string): void
    {
        this.getNode<T>().innerHTML = content;
    }

    append<T extends Element>(item: NItem): void
    {
        this.getNode<T>().appendChild(item.getNode() as Element);
    }

    getNode<T>(): T
    {
        return this.#node as T;
    }

    hasClass(className: string): boolean
    {
        return this.#node && this.#node.classList.contains(className);
    }

    addClass(className: string): void
    {
        this.#node.classList.add(className);
    }

    removeClass(className: string): void
    {
        this.#node.classList.remove(className);
    }

    on(
        eventName: string,
        cb: (e: Event) => void,
        opts: AddEventListenerOptions = {}
    ): NItem
    {
        const [type, namespace = "_n"] = eventName.split(".");
        const key = `${type}.${namespace}`;

        let handlers = nEventRegistry.get(this.#node);
        if (!handlers) {
            handlers = new Map();
            nEventRegistry.set(this.#node, handlers);
        }

        let set = handlers.get(key);
        if (!set) {
            set = new Set();
            handlers.set(key, set);
        }

        let listener = cb;
        const entry: NEventEntry = { listener, type };
        if (opts?.once) {
            listener = (e: Event) => {
                set!.delete(entry);
                if (set!.size === 0) handlers!.delete(key);
                cb(e);
            };
            entry.listener = listener;
        }

        set.add(entry);
        this.#node.addEventListener(type, listener, opts);
        return this;
    }

    off(eventName: string): NItem
    {
        const handlers = nEventRegistry.get(this.#node);
        if (!handlers) return this;

        const [type, namespace] = eventName.split(".");

        const removeKey = (key: string) => {
            const set = handlers.get(key);
            if (!set) return;
            for (const entry of set) {
                this.#node.removeEventListener(entry.type, entry.listener);
            }
            handlers.delete(key);
        };

        if (type === "") {
            for (const key of handlers.keys()) {
                if (key.endsWith(`.${namespace}`)) removeKey(key);
            }
        } else if (namespace === undefined) {
            removeKey(`${type}._n`);
        } else {
            removeKey(`${type}.${namespace}`);
        }

        if (handlers.size === 0) nEventRegistry.delete(this.#node);
        return this;
    }

    debug(): NItemDebug
    {
        return {
            tag: this.#tag,
            attributes: this.getAttributes(),
        };
    }
}
